/**
 * AirDangerBrief — composite air-danger + rain-washout forecast for the Deep
 * South (Yala / Pattani / Narathiwat), sourced from AirDash (a sibling 24/7
 * service on this same Mac).
 *
 * Headline (always visible): Yala's danger band + a one-line washout/relief
 * forecast. The Pattani/Narathiwat regional comparison rows are the "detailed
 * data set" — collapsed by default via a native <details> (same pattern as
 * FloodBrief's river-forecast block / BuildingLegend).
 */

import type { AirDangerProvince, FallbackTier } from "@yala/shared";
import { PanelHeader } from "./PanelHeader";
import { useT } from "../hooks/useLang";

interface Props {
  provinces: AirDangerProvince[];
  loading: boolean;
  ageMinutes?: number | null;
  fallbackTier?: FallbackTier;
}

const DANGER_BAND_COLOR: Record<AirDangerProvince["dangerBand"], string> = {
  normal: "var(--good)",
  watch: "var(--warn)",
  elevated: "var(--warn)",
  high: "var(--bad)",
  unknown: "var(--text-3)",
};

const WASHOUT_BAND_COLOR: Record<AirDangerProvince["washoutBand"], string> = {
  none: "var(--text-3)",
  light: "var(--data)",
  moderate: "var(--data)",
  strong: "var(--good)",
  unknown: "var(--text-3)",
};

function fmt(n: number | null | undefined, digits = 1, unit = ""): string {
  if (n == null || Number.isNaN(n)) return "—";
  return `${n.toFixed(digits)}${unit}`;
}

export function AirDangerBrief({ provinces, loading, ageMinutes, fallbackTier }: Props) {
  const t = useT();
  const yala = provinces.find((p) => p.provinceCode === "95") ?? provinces[0] ?? null;
  const others = provinces.filter((p) => p.provinceCode !== yala?.provinceCode);

  return (
    <div className="col flood-brief">
      <PanelHeader
        title={t("AIR DANGER & WASHOUT // DEEP SOUTH", "ความเสี่ยงอากาศ+พยากรณ์ฝนล้างฝุ่น // ชายแดนใต้")}
        source="airdash"
        ageMinutes={ageMinutes}
        fallbackTier={fallbackTier}
      />

      {loading || !yala ? (
        <div className="mono eyebrow" style={{ color: "var(--text-3)", marginTop: 8 }}>
          {loading ? t("Connecting…", "กำลังเชื่อมต่อ…") : t("no data", "ไม่มีข้อมูล")}
        </div>
      ) : (
        <>
          <div className="flood-row" style={{ display: "flex", gap: 12, marginTop: 8 }}>
            <div style={{ flex: 1 }}>
              <div className="eyebrow">{t("YALA DANGER", "ความเสี่ยงยะลา")}</div>
              <div className="mono" style={{ color: DANGER_BAND_COLOR[yala.dangerBand], fontSize: "0.9rem" }}>
                {yala.dangerBand.toUpperCase()}
              </div>
              <div className="eyebrow mono" style={{ color: "var(--text-3)" }}>
                PM2.5 {fmt(yala.pm25Live, 1, " µg/m³")} · score {fmt(yala.dangerScore, 0)}
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <div className="eyebrow">{t("RAIN WASHOUT", "ฝนล้างฝุ่น")}</div>
              <div className="mono" style={{ color: WASHOUT_BAND_COLOR[yala.washoutBand], fontSize: "0.9rem" }}>
                {yala.washoutBand.toUpperCase()}
              </div>
              <div className="eyebrow mono" style={{ color: "var(--text-3)" }}>
                {yala.expectedReliefPct != null
                  ? t(`~${fmt(yala.expectedReliefPct, 0)}% relief expected`, `คาดว่าฝุ่นลดลง ~${fmt(yala.expectedReliefPct, 0)}%`)
                  : t("no relief forecast", "ไม่มีข้อมูลคาดการณ์")}
              </div>
            </div>
          </div>

          {others.length > 0 && (
            <details className="detail-disclosure">
              <summary className="mono">{t("Pattani / Narathiwat comparison", "เทียบปัตตานี / นราธิวาส")}</summary>
              <div className="detail-disclosure-body">
                {others.map((p) => (
                  <div key={p.provinceCode} className="flood-row" style={{ display: "flex", gap: 12, marginTop: 4 }}>
                    <div style={{ flex: 1 }}>
                      <div className="eyebrow mono">{p.provinceNameEn || p.provinceNameTh}</div>
                      <div className="mono" style={{ color: DANGER_BAND_COLOR[p.dangerBand], fontSize: "0.82rem" }}>
                        {p.dangerBand.toUpperCase()} · PM2.5 {fmt(p.pm25Live, 1)}
                      </div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div className="eyebrow mono" style={{ color: "var(--text-3)" }}>{t("washout", "ล้างฝุ่น")}</div>
                      <div className="mono" style={{ color: WASHOUT_BAND_COLOR[p.washoutBand], fontSize: "0.82rem" }}>
                        {p.washoutBand.toUpperCase()}{p.expectedReliefPct != null ? ` · ${fmt(p.expectedReliefPct, 0)}%` : ""}
                      </div>
                    </div>
                  </div>
                ))}
                <div className="eyebrow mono" style={{ color: "var(--text-3)", marginTop: 6 }}>
                  {t(
                    "GISTDA satellite PM2.5 second-opinion: ",
                    "PM2.5 จากดาวเทียม GISTDA (ความเห็นที่สอง): ",
                  )}
                  {fmt(yala.pm25Gistda, 1, " µg/m³")}{yala.pm25Pcd != null ? ` (PCD ground: ${fmt(yala.pm25Pcd, 1)})` : ""}
                </div>
              </div>
            </details>
          )}
        </>
      )}

      <div className="eyebrow mono" style={{ color: "var(--text-3)", marginTop: 8 }}>
        {t(
          "AIRDASH — DANGER SCORE (HEAT+HUMIDITY+PM2.5+RAIN) · DOES NOT DUPLICATE AIR4THAI",
          "AIRDASH — คะแนนความเสี่ยง (ความร้อน+ความชื้น+PM2.5+ฝน) · ไม่ซ้ำกับ AIR4THAI",
        )}
      </div>
    </div>
  );
}
